import clsx from 'clsx';
import { memo, MouseEventHandler, PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type Props = PropsWithChildren<{
    className?: string;
    opened: boolean;
    handleClose: () => void;
    root: Element;
    onScroll?: () => void;
}>

export const Sidebar = memo(({
    opened,
    handleClose,
    children,
    root,
    className,
    onScroll,
}: Props) => {
    const sidebarRef = useRef<HTMLDivElement | null>(null);

    const clearFocusedElement = useCallback(() => {
        const focusedElement = document.activeElement;

        if (focusedElement instanceof HTMLElement) {
            focusedElement.blur();
        }
    }, []);

    const handleOverlayClick: MouseEventHandler<HTMLDivElement> = (event) => {
        const target = event.target as Node;
        event.stopPropagation();

        if (!sidebarRef.current?.contains(target)) {
            clearFocusedElement();
            handleClose();
        }
    };

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape' && opened) {
            clearFocusedElement();
            handleClose();
        }
    }, [clearFocusedElement, handleClose, opened]);

    useEffect(() => {
        document.body.classList.toggle('vss-overflow-hidden', opened);

        return () => {
            document.body.classList.remove('vss-overflow-hidden');
        };
    }, [opened]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    return root ? createPortal(
        <div
            className={clsx(
                {
                    'vss-sidebar-overlay': true,
                    'vss-sidebar-overlay--visible': opened,
                },
                className,
            )}
            onMouseDown={handleOverlayClick}
            role="presentation"
            aria-hidden={!opened}
        >
            <div
                onScroll={onScroll}
                ref={sidebarRef}
                className={clsx(
                    {
                        'vss-sidebar': true,
                        'vss-sidebar--visible': opened,
                    },
                )}
            >{children}
            </div>
        </div>,
        root,
    ) : null;
});
